/// <reference lib="dom"/>
import * as rxjs from "rxjs";
import { getLogger } from "@logtape/logtape";
import {
  type ActionHandlers,
  Dispatcher,
  type DispatcherOptions,
  type Message,
} from "@utils/dispatch";
import { type WbaCtx, type WbaCtxStatus, WbaMic$ } from "@/context/wbactx.tsx";
import {
  FxChain,
  FxLoader,
  type FxSlot,
  FxSlotAdd,
  FxSlotBypass,
  FxSlotMove,
  FxSlotOpts,
  FxSlotRemove,
} from "./fxChain.tsx";
import type { LiveEffectType } from "./effects.tsx";

//#region dispatch
export interface FxPipelineState {
  "io/in": number;
  "io/out": number;
  fx: FxSlot[];
  error: string | null;
}

export const FxPipelineDefaultState: FxPipelineState = {
  "io/in": 1,
  "io/out": 1,
  fx: FxSlotAdd(FxSlotAdd([], "compressor"), "chorus"),
  error: null,
};

export interface FxPipelineActions {
  "io/in": { gain: number };
  "io/out": { gain: number };
  "fx/add": { type: LiveEffectType; opts?: Record<string, unknown> };
  "fx/remove": { slot: string };
  "fx/move": { slot: string; by: number };
  "fx/moveTo": { from: number; to: number };
  "fx/bypass": { slot: string };
  "fx/opts": { slot: string; opts: Record<string, unknown> };
  "fx/set": { fx: FxSlot[] };
  "fx/error": { message: string };
}

export type FxPipelineMessage = Message<FxPipelineActions>;
type Handlers = ActionHandlers<FxPipelineActions, FxPipelineState>;
//#endregion

//#region utils
const GainMax = 5;

function Clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

//#endregion

export interface FxPipelineProps {
  wbactx: WbaCtx;
  source$?: rxjs.Observable<AudioNode | null>;
  path?: string;
  base?: string;
  initial?: Partial<FxPipelineState>;
  dispatcher?: DispatcherOptions;
}

interface FxPipelineNodes {
  input: GainNode;
  output: GainNode;
  fx: FxChain;
  source: AudioNode | null;
}

export class FxPipeline extends Dispatcher<FxPipelineActions, FxPipelineState, FxPipelineProps> {
  protected readonly handlers: Handlers = {
    "io/in": ({ state, payload }) => ({ ...state, "io/in": Clamp(payload.gain, 0, GainMax) }),
    "io/out": ({ state, payload }) => ({ ...state, "io/out": Clamp(payload.gain, 0, GainMax) }),
    "fx/add": ({ state, payload }) => ({
      ...state,
      fx: FxSlotAdd(state.fx, payload.type, payload.opts),
    }),
    "fx/remove": ({ state, payload }) => ({ ...state, fx: FxSlotRemove(state.fx, payload.slot) }),
    "fx/move": ({ state, payload }) => ({
      ...state,
      fx: FxSlotMove(state.fx, payload.slot, payload.by),
    }),
    "fx/moveTo": ({ state, payload }) => {
      const slot = state.fx[payload.from];
      if (!slot) return state;
      return { ...state, fx: FxSlotMove(state.fx, slot.id, payload.to - payload.from) };
    },
    "fx/bypass": ({ state, payload }) => ({ ...state, fx: FxSlotBypass(state.fx, payload.slot) }),
    "fx/opts": ({ state, payload }) => ({
      ...state,
      fx: FxSlotOpts(state.fx, payload.slot, payload.opts),
    }),
    "fx/set": ({ state, payload }) => ({ ...state, fx: payload.fx }),
    "fx/error": ({ state, payload }) => ({ ...state, error: payload.message }),
  };

  #logger = getLogger(["webaudio", "fxPipeline"]);
  #subs = new rxjs.Subscription();
  #fx: FxLoader;
  #nodes: FxPipelineNodes | null = null;

  constructor(props: FxPipelineProps) {
    super(props, { ...FxPipelineDefaultState, ...props.initial }, props.dispatcher);
    const { wbactx } = props;
    this.#fx = new FxLoader({ wbactx, base: props.base });

    this.#subs.add(
      this.#fx.error$.subscribe((message) => {
        this.#logger.warn`fx: ${message}`;
        this.dispatch({ type: "fx/error", payload: { message } });
      }),
    );
    this.#subs.add(
      rxjs.combineLatest([
        this.select$((s) => s.fx),
        this.select$((s) => s["io/in"]),
        this.select$((s) => s["io/out"]),
        wbactx.select$((s) => s["ctx/status"]),
        props.source$ ?? WbaMic$(wbactx),
        this.#fx.changed$,
      ]).subscribe(([fx, gainIn, gainOut, status, source]) =>
        this.#sync(fx, gainIn, gainOut, status, source)
      ),
    );
  }

  get path(): string {
    return this.props.path ?? "/fx";
  }

  get input(): AudioNode | null {
    return this.#nodes?.input ?? null;
  }

  get output(): AudioNode | null {
    return this.#nodes?.output ?? null;
  }

  //#region audio
  #sync(
    fx: FxSlot[],
    gainIn: number,
    gainOut: number,
    status: WbaCtxStatus,
    source: AudioNode | null,
  ) {
    if (status === "uninit" || status === "closed") {
      this.#nodes?.fx.dispose();
      this.#nodes = null;
      return;
    }

    const { wbactx } = this.props;
    const fresh = !this.#nodes;
    const nodes = this.#nodes ?? this.#create();
    const now = wbactx.now;
    if (fresh) {
      nodes.input.gain.value = gainIn;
      nodes.output.gain.value = gainOut;
    } else {
      nodes.input.gain.setTargetAtTime(gainIn, now, 0.01);
      nodes.output.gain.setTargetAtTime(gainOut, now, 0.01);
    }

    if (source !== nodes.source) {
      if (nodes.source) {
        try {
          nodes.source.disconnect(nodes.input);
        } catch (_e) {
          void _e;
        }
      }
      source?.connect(nodes.input);
      nodes.source = source;
    }

    nodes.fx.render(fx);
  }

  #create(): FxPipelineNodes {
    const { wbactx } = this.props;
    const path = this.path;
    const input = wbactx.node((c) => c.createGain(), { id: `${path}/in`, name: "fx in" });
    const output = wbactx.node((c) => c.createGain(), { id: `${path}/out`, name: "fx out" });
    output.connect(wbactx.output);
    const fx = new FxChain({ loader: this.#fx, input, output, path: `${path}/chain` });
    this.#nodes = { input, output, fx, source: null };
    return this.#nodes;
  }

  //#endregion

  override dispose() {
    if (this.disposed) return;
    this.#subs.unsubscribe();
    const nodes = this.#nodes;
    if (nodes) {
      nodes.fx.dispose();
      const { wbactx } = this.props;
      if (nodes.source) {
        try {
          nodes.source.disconnect(nodes.input);
        } catch (_e) {
          void _e;
        }
      }
      wbactx.release(nodes.input);
      wbactx.release(nodes.output);
    }
    this.#nodes = null;
    this.#fx.dispose();
    super.dispose();
  }
}
